import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import apiUrl from "../Apiurl";
import { BlockSkeleton } from "../interfaccia/loading";
import stilePulsanti from "../interfaccia/stilePulsanti";
import "./TraduzioniPage.css";

// Glossario traduzioni gestito da Domino (T29, D18). In traduzione vale solo
// la versione pubblicata: le modifiche restano "da pubblicare" finche' non si
// preme "Pubblica".

const BASE = `${apiUrl}/prodotti/glossario`;
const POLL_MS = 3000;
const STATI = { IN_CORSO: "In corso", ATTIVA: "Attiva", SUPERATA: "Superata", FALLITA: "Fallita", RIMOSSA: "Rimossa" };
const PASSI = {
  PREPARAZIONE: "Preparazione", CARICAMENTO_CSV: "Caricamento del CSV", CREAZIONE_GLOSSARIO: "Creazione del glossario su Google",
  VERIFICA: "Verifica dei termini",
};
const CONFERMA_PUBBLICA = "Il glossario pubblicato vale per tutti gli ambienti che usano questo database: oggi beta e produzione condividono Mongo. Pubblicare?";
// Contesto MODA di una voce (D20): vale solo per gli articoli con questi codici.
const DIMENSIONI = [["reparto", "Reparto", "reparti"], ["categoria", "Categoria", "categorie"], ["tipo", "Tipo", "tipi"], ["sottotipo", "Sottotipo", "sottotipi"]];
const CONTESTO_VUOTO = { reparto: "", categoria: "", tipo: "", sottotipo: "" };
const NUOVA = { termineIt: "", lingua: "en", traduzione: "", interaStringa: true, contesto: CONTESTO_VUOTO };
// #806: Google inserisce la voce cosi' com'e', quindi una maiuscola resta anche a meta' frase;
// con la voce minuscola adatta da solo le maiuscole. Per la stringa intera l'API rimette l'iniziale.
const ID_NOTA_MINUSCOLO = "glossario-nota-minuscolo";
const NOTA_MINUSCOLO = "Scrivi la traduzione in minuscolo, salvo nomi propri e sigle («ski de randonnée», non «Ski de randonnée»): "
  + "Google mette da solo la maiuscola a inizio frase e nei titoli, mentre una maiuscola nella voce resta anche a metà frase. "
  + "Quando il campo coincide con il termine, Domino rimette l'iniziale maiuscola.";

const breve = (versione) => (versione ? String(versione).slice(0, 8) : "—");
const data = (value) => (value ? new Date(value).toLocaleString("it-IT") : "—");
export function etichettaContesto(value, lookups = {}) {
  if (!value || !Object.keys(value).length) return "Tutti gli articoli";
  return DIMENSIONI.filter(([key]) => value[key]).map(([key, label]) => {
    const descrizione = lookups[key]?.find((item) => item.code === value[key])?.description;
    return `${label}: ${value[key]}${descrizione && descrizione !== value[key] ? ` (${descrizione})` : ""}`;
  }).join(" · ");
}
const passo = (value) => {
  const match = /^([A-Z_]+)(.*)$/.exec(value || "");
  return match ? `${PASSI[match[1]] || match[1]}${match[2]}` : "avvio";
};
const messaggio = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

function Differenze({ differenze, lookups }) {
  if (!differenze) return null;
  const gruppi = [["aggiunte", "Aggiunte"], ["modificate", "Modificate"], ["disattivate", "Disattivate"]]
    .filter(([key]) => differenze[key]?.length);
  if (!gruppi.length) return null;
  return <div className="f6 mt2" aria-label="Differenze dalla versione attiva">
    {gruppi.map(([key, label]) => <details key={key} className="mb1">
      <summary>{label}: {differenze[key].length}</summary>
      <ul>{differenze[key].map((voce) => <li key={voce.id}>{voce.termineIt} ({voce.lingua.toUpperCase()}) → {voce.traduzione}
        {voce.contesto && <span className="f7"> · {etichettaContesto(voce.contesto, lookups)}</span>}</li>)}</ul>
    </details>)}
  </div>;
}

function RigaVoce({ voce, canEdit, salva, busy, lookups }) {
  const [bozza, setBozza] = useState(null);
  const inModifica = bozza !== null;
  const conferma = async () => { if (await salva(voce._id, bozza)) setBozza(null); };
  return <tr className={voce.attivo ? undefined : "o-60"}>
    <th scope="row">{voce.termineIt}{voce.variantiIt?.length > 1 && <div className="f7 fw4">Varianti: {voce.variantiIt.join(", ")}</div>}</th>
    <td>{voce.lingua.toUpperCase()}</td>
    <td>{inModifica
      ? <input aria-label={`Traduzione di ${voce.termineIt} (${voce.lingua})`} aria-describedby={ID_NOTA_MINUSCOLO} className={stilePulsanti.inputClassesNoMeasure}
        value={bozza.traduzione} onChange={(event) => setBozza({ ...bozza, traduzione: event.target.value })} />
      : voce.traduzione}</td>
    <td className="f7">{etichettaContesto(voce.contesto, lookups)}</td>
    <td>{inModifica
      ? <input type="checkbox" aria-label={`Stringa intera per ${voce.termineIt} (${voce.lingua})`} checked={bozza.interaStringa}
        onChange={(event) => setBozza({ ...bozza, interaStringa: event.target.checked })} />
      : voce.interaStringa ? "Sì" : "No"}</td>
    <td>{voce.attivo ? "Attiva" : "Disattivata"}</td>
    <td className="f7">{voce.audit?.modificatoDa || voce.audit?.username || "—"}<div>{data(voce.audit?.modificatoIl || voce.audit?.importatoIl)}</div></td>
    {canEdit && <td>{inModifica ? <>
      <button className={stilePulsanti.buttonClassesWhite} disabled={busy || !bozza.traduzione.trim()} onClick={conferma}>Salva</button>
      <button className={stilePulsanti.buttonClassesWhite} disabled={busy} onClick={() => setBozza(null)}>Annulla</button>
    </> : <>
      <button className={stilePulsanti.buttonClassesWhite} disabled={busy}
        onClick={() => setBozza({ traduzione: voce.traduzione, interaStringa: voce.interaStringa })}>Modifica</button>
      <button className={stilePulsanti.buttonClassesWhite} disabled={busy}
        onClick={() => salva(voce._id, { attivo: !voce.attivo })}>{voce.attivo ? "Disattiva" : "Riattiva"}</button>
    </>}</td>}
  </tr>;
}

export default function GlossarioPage({ utente }) {
  const { getAccessTokenSilently } = useAuth0();
  const permessi = utente?.permessiAPI || {};
  const canRead = Boolean(permessi["read:database"]);
  const canEdit = Boolean(permessi["update:glossario"]);
  const canPublish = canEdit && Boolean(permessi["usage:googleapis"]);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [filtri, setFiltri] = useState({ testo: "", lingua: "", stato: "attive", contesto: "" });
  const [lookups, setLookups] = useState({});
  const [nuova, setNuova] = useState(NUOVA);
  // Pubblicazione avviata da questa pagina: il suo esito si mostra in alto.
  const [seguita, setSeguita] = useState(null);

  const richiesta = useCallback(async (method, path = "", body) => {
    const token = await getAccessTokenSilently();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = method === "get" ? await axios.get(`${BASE}${path}`, config) : await axios[method](`${BASE}${path}`, body ?? {}, config);
    if (!response.data?.success) throw new Error("Risposta del glossario non valida.");
    return response.data.data;
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!canRead) return undefined;
    let active = true;
    (async () => {
      try {
        const value = await richiesta("get");
        if (!active) return;
        if (!value?.stato || !Array.isArray(value.voci)) throw new Error("Risposta del glossario incompleta.");
        setSnapshot(value);
        setError("");
      } catch (err) {
        if (active) setError(messaggio(err, "Errore durante la lettura del glossario."));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [canRead, richiesta, refresh]);

  // Codici MODA per le tendine del contesto; senza elenco si scrive il codice.
  useEffect(() => {
    if (!canRead) return undefined;
    let active = true;
    (async () => {
      const token = await getAccessTokenSilently().catch(() => null);
      if (!token || !active) return;
      const risultati = await Promise.allSettled(DIMENSIONI.map(([, , tipo]) =>
        axios.get(`${apiUrl}/moda/lookup/${tipo}`, { headers: { Authorization: `Bearer ${token}` } })));
      if (!active) return;
      setLookups(Object.fromEntries(DIMENSIONI.map(([key], index) => {
        const data = risultati[index].status === "fulfilled" ? risultati[index].value?.data?.data : null;
        return [key, Array.isArray(data) ? data.filter((item) => item && typeof item.code === "string" && item.code.trim()) : []];
      })));
    })();
    return () => { active = false; };
  }, [canRead, getAccessTokenSilently]);

  const inCorso = snapshot?.stato?.inCorso;
  useEffect(() => {
    if (!inCorso) return undefined;
    const timer = setTimeout(() => setRefresh((value) => value + 1), POLL_MS);
    return () => clearTimeout(timer);
  }, [inCorso, snapshot]);

  useEffect(() => {
    const pubblicazione = seguita && snapshot?.stato?.storico?.find((item) => item._id === seguita);
    if (!pubblicazione || pubblicazione.stato === "IN_CORSO") return;
    setSeguita(null);
    if (pubblicazione.stato === "FALLITA") {
      setNotice("");
      setError(`Pubblicazione non riuscita: ${pubblicazione.errore || "errore sconosciuto"}. La versione attiva non è cambiata.`);
    } else {
      setNotice(`Pubblicazione completata: versione ${breve(pubblicazione.versione)} attiva.`);
    }
  }, [seguita, snapshot]);

  const voci = useMemo(() => {
    const testo = filtri.testo.trim().toLowerCase();
    return (snapshot?.voci || []).filter((voce) => (!filtri.lingua || voce.lingua === filtri.lingua) &&
      (filtri.stato === "tutte" || (filtri.stato === "attive") === voce.attivo) &&
      (!filtri.contesto || (filtri.contesto === "con") === Boolean(voce.contesto && Object.keys(voce.contesto).length)) &&
      (!testo || voce.termineIt.toLowerCase().includes(testo) || voce.traduzione.toLowerCase().includes(testo)));
  }, [snapshot, filtri]);

  const esegui = async (azione, conferma) => {
    setBusy(true);
    setNotice("");
    try {
      const esito = await azione();
      if (conferma) setNotice(conferma(esito));
      setError("");
      setRefresh((value) => value + 1);
      return true;
    } catch (err) {
      setError(messaggio(err, "Operazione sul glossario non riuscita."));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const salva = (id, modifiche) => esegui(() => richiesta("patch", `/voci/${encodeURIComponent(id)}`, modifiche), () => "Voce salvata: da pubblicare.");
  const crea = async (event) => {
    event.preventDefault();
    const contestoNuova = Object.fromEntries(Object.entries(nuova.contesto).map(([key, value]) => [key, value.trim()]).filter(([, value]) => value));
    if (await esegui(() => richiesta("post", "/voci", { ...nuova, termineIt: nuova.termineIt.trim(), traduzione: nuova.traduzione.trim(),
      contesto: Object.keys(contestoNuova).length ? contestoNuova : null }),
      () => "Voce aggiunta: da pubblicare.")) setNuova(NUOVA);
  };
  const pubblica = () => {
    if (!window.confirm(CONFERMA_PUBBLICA)) return;
    esegui(() => richiesta("post", "/pubblicazioni"), (esito) => {
      if (esito?.esito === "NULLA_DA_PUBBLICARE") return "Nessuna modifica da pubblicare.";
      setSeguita(esito?.pubblicazione?._id || null);
      return "Pubblicazione avviata: l'esito comparirà qui.";
    });
  };
  const ripristina = (pubblicazione) => {
    if (!window.confirm(`Ripristinare la versione ${breve(pubblicazione.versione)}? ${CONFERMA_PUBBLICA}`)) return;
    esegui(() => richiesta("post", `/pubblicazioni/${encodeURIComponent(pubblicazione._id)}/ripristina`),
      () => `Versione ${breve(pubblicazione.versione)} ripristinata.`);
  };

  if (!canRead) return <div className="pa3" role="alert">Per consultare il glossario serve il permesso read:database.</div>;
  const stato = snapshot?.stato;
  const attiva = stato?.attiva;
  const ultima = stato?.storico?.[0];
  const ultimaFallita = !seguita && ultima?.stato === "FALLITA" ? ultima : null;
  return <div className="traduzioni-page pa3">
    <div className="traduzioni-heading"><h1>Glossario traduzioni</h1>
      {canPublish && <button className={stilePulsanti.buttonClassesWhite} onClick={pubblica}
        disabled={busy || loading || !stato?.daPubblicare || Boolean(inCorso) || Boolean(stato?.vociNonValide)}>Pubblica</button>}
    </div>
    <p>Le traduzioni usano solo la versione pubblicata. Le modifiche alle voci valgono dalla pubblicazione successiva.</p>
    {!canEdit && <p className="f6" role="note">Sola lettura: per modificare le voci serve il permesso update:glossario.</p>}
    {canEdit && !canPublish && <p className="f6" role="note">Per pubblicare serve anche il permesso usage:googleapis.</p>}
    {error && <p role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {loading ? <BlockSkeleton height={320} /> : stato && <>
      <section className="sfondoBiancoTema ba bordoColAlt br3 pa3 mb3" aria-label="Versione attiva">
        {attiva ? <p className="mt0">Versione attiva <strong>{breve(attiva.versione)}</strong> · {attiva.glossaryId} · {attiva.voci} voci ·
          attivata il {data(attiva.attivataIl)}{attiva.origine === "BOOTSTRAP" ? " (glossario iniziale)" : ""}</p>
          : <p className="mt0">Nessuna pubblicazione: valgono le variabili iniziali del glossario.</p>}
        {stato.vociNonValide && <p role="alert">Le voci attuali non sono valide e non si possono pubblicare: {stato.vociNonValide}</p>}
        {stato.daPubblicare && <span className="traduzioni-badge ba bordoColAlt">Da pubblicare</span>}
        {inCorso && <p role="status">Pubblicazione in corso: {passo(inCorso.passo)}…</p>}
        {ultimaFallita && <p role="note">L'ultima pubblicazione ({data(ultimaFallita.creataIl)}) non è riuscita: {ultimaFallita.errore || "errore sconosciuto"}.</p>}
        <Differenze differenze={stato.differenze} lookups={lookups} />
      </section>

      {canEdit && <form className="traduzioni-filters" onSubmit={crea} aria-label="Nuova voce">
        <label>Termine italiano<input className={stilePulsanti.inputClassesNoMeasure} value={nuova.termineIt}
          onChange={(event) => setNuova({ ...nuova, termineIt: event.target.value })} /></label>
        <label>Lingua<select className={stilePulsanti.inputClassesNoMeasure} value={nuova.lingua}
          onChange={(event) => setNuova({ ...nuova, lingua: event.target.value })}><option value="en">EN</option><option value="fr">FR</option></select></label>
        <label>Traduzione<input className={stilePulsanti.inputClassesNoMeasure} value={nuova.traduzione} aria-describedby={ID_NOTA_MINUSCOLO}
          onChange={(event) => setNuova({ ...nuova, traduzione: event.target.value })} /></label>
        <label className="flex-row items-center"><input type="checkbox" checked={nuova.interaStringa}
          onChange={(event) => setNuova({ ...nuova, interaStringa: event.target.checked })} /> Anche come stringa intera</label>
        <p id={ID_NOTA_MINUSCOLO} className="f6 w-100 mv0">{NOTA_MINUSCOLO}</p>
        <fieldset className="traduzioni-filters ba bordoColAlt br2 pa2 w-100" aria-label="Contesto della nuova voce">
          <legend className="f6">Contesto (facoltativo): la voce vale solo per gli articoli con questi codici MODA</legend>
          {DIMENSIONI.map(([key, label]) => <label key={key}>{label}{lookups[key]?.length
            ? <select className={stilePulsanti.inputClassesNoMeasure} value={nuova.contesto[key]}
              onChange={(event) => setNuova({ ...nuova, contesto: { ...nuova.contesto, [key]: event.target.value } })}>
              <option value="">Tutti</option>
              {lookups[key].map((item) => <option key={item.code} value={item.code}>{item.description || item.code} ({item.code})</option>)}
            </select>
            : <input className={stilePulsanti.inputClassesNoMeasure} value={nuova.contesto[key]} placeholder="Codice MODA"
              onChange={(event) => setNuova({ ...nuova, contesto: { ...nuova.contesto, [key]: event.target.value } })} />}</label>)}
        </fieldset>
        <button className={stilePulsanti.buttonClassesWhite} type="submit" disabled={busy || !nuova.termineIt.trim() || !nuova.traduzione.trim()}>Aggiungi voce</button>
      </form>}

      <div className="traduzioni-filters" aria-label="Filtri glossario">
        <label>Cerca<input className={stilePulsanti.inputClassesNoMeasure} value={filtri.testo}
          onChange={(event) => setFiltri({ ...filtri, testo: event.target.value })} /></label>
        <label>Lingua<select className={stilePulsanti.inputClassesNoMeasure} value={filtri.lingua}
          onChange={(event) => setFiltri({ ...filtri, lingua: event.target.value })}><option value="">Tutte</option><option value="en">EN</option><option value="fr">FR</option></select></label>
        <label>Stato<select className={stilePulsanti.inputClassesNoMeasure} value={filtri.stato}
          onChange={(event) => setFiltri({ ...filtri, stato: event.target.value })}>
          <option value="attive">Attive</option><option value="disattivate">Disattivate</option><option value="tutte">Tutte</option></select></label>
        <label>Contesto<select className={stilePulsanti.inputClassesNoMeasure} value={filtri.contesto}
          onChange={(event) => setFiltri({ ...filtri, contesto: event.target.value })}>
          <option value="">Tutte le voci</option><option value="generali">Solo generali</option><option value="con">Solo con contesto</option></select></label>
      </div>
      <div className="traduzioni-table-scroll">
        <table className="traduzioni-articles"><caption>Voci ({voci.length})</caption>
          <thead><tr><th>Termine</th><th>Lingua</th><th>Traduzione</th><th>Contesto</th><th>Stringa intera</th><th>Stato</th><th>Ultima modifica</th>
            {canEdit && <th>Azioni</th>}</tr></thead>
          <tbody>{voci.map((voce) => <RigaVoce key={voce._id} voce={voce} canEdit={canEdit} salva={salva} busy={busy} lookups={lookups} />)}</tbody>
        </table>
        {!voci.length && <p>Nessuna voce con questi filtri.</p>}
      </div>

      <div className="traduzioni-table-scroll mt3">
        <table className="traduzioni-articles"><caption>Pubblicazioni</caption>
          <thead><tr><th>Creata</th><th>Versione</th><th>Glossario Google</th><th>Stato</th><th>Autore</th><th>Note</th>{canPublish && <th />}</tr></thead>
          <tbody>{(stato.storico || []).map((pubblicazione) => <tr key={pubblicazione._id}>
            <td>{data(pubblicazione.creataIl)}</td><td>{breve(pubblicazione.versione)}</td><td>{pubblicazione.glossaryId}</td>
            <td>{STATI[pubblicazione.stato] || pubblicazione.stato}</td><td>{pubblicazione.ripristinataDa || pubblicazione.autore}</td>
            <td className="f7">{pubblicazione.errore || (pubblicazione.entryCount !== null && pubblicazione.entryCount !== undefined
              ? `${pubblicazione.entryCount} termini` : "")}
              {pubblicazione.glossariContesto?.length > 0 && ` · più ${pubblicazione.glossariContesto.length} glossari per contesto`}</td>
            {canPublish && <td>{pubblicazione.stato === "SUPERATA" && <button className={stilePulsanti.buttonClassesWhite}
              disabled={busy || Boolean(inCorso)} onClick={() => ripristina(pubblicazione)}>Ripristina</button>}</td>}
          </tr>)}</tbody>
        </table>
      </div>
    </>}
  </div>;
}
